import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const CONTACT_INFO = [
  { icon: Mail, title: "Email Us", value: "info@afriperx.online", subtitle: "We reply within 24 hours" },
  { icon: MapPin, title: "Visit Us", value: "Johannesburg, Gauteng", subtitle: "South Africa" },
  { icon: Clock, title: "Business Hours", value: "Mon-Fri: 8am - 5pm", subtitle: "Sat: 9am - 1pm" },
];

const FAQS = [
  { q: "How do I sign up for AfriPerx?", a: "Click 'Get Started' and fill in your details. You'll receive an email verification, and once confirmed, you're in! The process takes less than 2 minutes." },
  { q: "Is there a membership fee?", a: "Basic membership is completely free. Premium tiers with additional benefits are available at competitive monthly rates." },
  { q: "How do I earn rewards?", a: "Simply shop at any of our 25+ partner stores and your rewards are automatically tracked. You can redeem them for cash, vouchers, or airtime." },
  { q: "Are my funds safe with AfriPerx?", a: "Absolutely. We use bank-grade security, are fully FSP licensed, and your funds are protected under South African financial regulations." },
];

export const ContactPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    await new Promise(r => setTimeout(r, 1500));
    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    setForm({ name: '', email: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Home</Link>
            <Link to="/about" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">About Us</Link>
            <Link to="/products" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Our Products</Link>
            <Link to="/contact" className="text-sm text-afriperx-green font-semibold">Contact Us</Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors">Sign In</button>
            <button onClick={() => navigate('/get-started')} className="px-5 py-2 text-sm bg-afriperx-green hover:bg-afriperx-green-dark text-white rounded-full font-medium transition-colors">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center pt-8">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-green bg-green-100 px-4 py-1.5 rounded-full mb-4">Contact Us</span>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">We'd Love to <span className="text-afriperx-green">Hear From You</span></h1>
          <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
            Have a question, suggestion, or need assistance? Our team is here to help.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          {CONTACT_INFO.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg hover:border-afriperx-gold/20 transition-all duration-300 text-center">
              <div className="w-11 h-11 rounded-xl bg-afriperx-green/10 flex items-center justify-center mx-auto mb-3">
                <c.icon className="w-5 h-5 text-afriperx-green" />
              </div>
              <h3 className="font-syne font-bold text-gray-900 text-sm mb-1">{c.title}</h3>
              <p className="text-afriperx-green font-medium text-sm">{c.value}</p>
              <p className="text-gray-400 text-xs mt-1">{c.subtitle}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form + FAQ */}
      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">
          {/* Form */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <MessageCircle className="w-5 h-5 text-afriperx-gold" />
              <h2 className="font-syne text-xl font-bold text-gray-900">Send Us a Message</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Name *</label>
                  <input
                    type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm transition-colors"
                    placeholder="Your name" data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Email *</label>
                  <input
                    type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm transition-colors"
                    placeholder="your@email.com" data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Subject</label>
                <input
                  type="text" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm transition-colors"
                  placeholder="What's this about?" data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Message *</label>
                <textarea
                  value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm transition-colors resize-none"
                  placeholder="Tell us how we can help..." data-testid="contact-message"
                />
              </div>
              <button
                type="submit" disabled={sending}
                className="w-full py-3 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit"
              >
                {sending ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
              </button>
            </form>
          </div>

          {/* FAQ */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <MessageCircle className="w-5 h-5 text-afriperx-gold" />
              <h2 className="font-syne text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <div key={i} className="rounded-xl border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-medium text-gray-900 text-sm pr-4">{faq.q}</span>
                    <span className={`text-afriperx-gold transition-transform duration-200 shrink-0 ${expandedFaq === i ? 'rotate-180' : ''}`}>
                      <ArrowRight className="w-4 h-4 rotate-90" />
                    </span>
                  </button>
                  {expandedFaq === i && (
                    <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed animate-fade-in">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={LOGO_URL} alt="AfriPerx" className="h-8 w-auto brightness-200" />
            <div className="flex gap-6">
              <Link to="/" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Home</Link>
              <Link to="/about" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">About</Link>
              <Link to="/products" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Products</Link>
              <Link to="/terms" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Terms</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 pt-6 text-center">
            <p className="text-gray-600 text-xs">&copy; 2026 AfriPerx. All rights reserved. FSP Licensed.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
